import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchAiCredits } from '@/api/ai';
import { fetchCompaniesToSwitch, switchCompany } from '@/api/companies';
import type { CompanyToSwitch } from '@/api/companies/types';
import { fetchMe } from '@/api/me';
import { useFeatures } from '@/hooks/useFeatures';
import { useTranslator } from '@/hooks/useTranslator';
import {
  avatarColor,
  getCompanyInitials,
  getInitials,
  imgUrl,
} from '@/lib/utils/avatar';
import { useActivityFeedStore } from '@/store/activityFeedStore';
import { useSessionStore } from '@/store/sessionStore';
import { useUploadStore } from '@/store/uploadStore';

export function useUserDropdown() {
  const user = useSessionStore(s => s.user);
  const setUser = useSessionStore(s => s.setUser);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creditsPopoverOpen, setCreditsPopoverOpen] = useState(false);

  const { isTracingAiEnabled } = useFeatures();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getTranslation } = useTranslator({ page: 'appHeader' });
  const setNotificationsOpen = useActivityFeedStore(s => s.setOpen);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-to-switch'],
    queryFn: fetchCompaniesToSwitch,
  });

  const { data: creditsResponse } = useQuery({
    queryKey: ['ai-credits'],
    queryFn: fetchAiCredits,
    enabled: isTracingAiEnabled,
    staleTime: 60_000,
  });
  const credits = creditsResponse?.data ?? null;

  const switchCompanyMutation = useMutation({
    mutationFn: (companyId: string) => switchCompany(companyId),
  });

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const isImpersonating = Boolean(
    JSON.parse(localStorage.getItem('retraced:context') ?? '{}')?.state
      ?.impersonateId,
  );

  async function handleSwitchCompany(company: CompanyToSwitch) {
    try {
      await switchCompanyMutation.mutateAsync(company.companyId);
      const me = await fetchMe();
      if (me) setUser(me);
      // Clear entire query cache — all data is scoped to the old company context.
      queryClient.clear();
      setOpen(false);
      void navigate({ to: '/dashboard' });
    } catch {
      // switch failed — user remains on current company
    }
  }

  function handleEndImpersonate() {
    useUploadStore.getState().clearAll();
    // Clear the impersonated session: in-memory + persisted `me` and the auth
    // context (tokens + impersonateId). Otherwise a stale impersonated session
    // lingers in v2 localStorage for the next visit.
    useSessionStore.getState().clearUser();
    localStorage.removeItem('retraced:context');
    const oldFrontend =
      import.meta.env.VITE_OLD_FRONTEND_URL ?? 'http://localhost:3500';
    window.location.href = `${oldFrontend}/management/impersonate?endImpersonate=1`;
  }

  const currentCompanyRole = user?.role?.toLowerCase().replace(/_/g, ' ');
  const initials = user ? getInitials(user.firstName, user.lastName) : '';
  const companyInitials = user?.company?.name
    ? getCompanyInitials(user.company.name)
    : '';

  return {
    user,
    open,
    setOpen,
    companies,
    search,
    setSearch,
    credits,
    creditsPopoverOpen,
    setCreditsPopoverOpen,
    isTracingAiEnabled,
    navigate,
    getTranslation,
    setNotificationsOpen,
    filtered,
    isImpersonating,
    handleSwitchCompany,
    handleEndImpersonate,
    currentCompanyRole,
    initials,
    companyInitials,
    avatarColor,
    getCompanyInitials,
    imgUrl,
  };
}
